/**
 * RerunWrapper — picks the right per-type rerun panel based on
 * report.reportType. Workflow Prompt 6.
 *
 * All panels share the same shell (FeasibilityRerunPanel as the base);
 * each per-type panel passes a domain-specific gap categorizer. Config-form
 * override (per-type preference sub-form) is the deferred follow-up — for
 * now every type uses the projectIntent + customWeights config form.
 */
import React from 'react';
import type { SiteRagReport } from '../../types/siteRag';
import { FeasibilityRerunPanel } from './rerun/FeasibilityRerunPanel';
import { EnvironmentalRerunPanel } from './rerun/EnvironmentalRerunPanel';
import { PowerAvailabilityRerunPanel } from './rerun/PowerAvailabilityRerunPanel';
import { WaterResourceRerunPanel } from './rerun/WaterResourceRerunPanel';
import { TestFitRerunPanel } from './rerun/TestFitRerunPanel';
import { InterconnectionRerunPanel } from './rerun/InterconnectionRerunPanel';

interface Props {
  report: SiteRagReport;
  open: boolean;
  onClose: () => void;
}

export function RerunWrapper(props: Props) {
  const reportType = props.report.reportType ?? 'feasibility';
  // RWX-bleed — key the panel by the report id. `SiteRagReportView` (and its
  // parent `SiteRagEngine`) are NOT keyed per report, so switching the active
  // report reuses the same panel instance. Without this key, a prior report's
  // in-flight rerun (which set the panel's internal `step` to 'running') leaks
  // into a *different* report's rerun, so opening rerun on report B while
  // report A is still generating skipped B's setup and dropped straight into
  // the generation view. Keying by report.id forces a fresh mount per report
  // so `step` re-seeds to 'gaps'/'config'. Safe mid-run: `report.id` is the
  // stable parent id throughout a rerun.
  const key = props.report.id;
  switch (reportType) {
    case 'environmental':       return <EnvironmentalRerunPanel key={key} {...props} />;
    case 'power-availability':  return <PowerAvailabilityRerunPanel key={key} {...props} />;
    case 'water-resource':      return <WaterResourceRerunPanel key={key} {...props} />;
    case 'test-fit':            return <TestFitRerunPanel key={key} {...props} />;
    case 'interconnection':     return <InterconnectionRerunPanel key={key} {...props} />;
    case 'feasibility':
    default:                    return <FeasibilityRerunPanel key={key} {...props} />;
  }
}
